import React, {useState, useEffect} from 'react';
import { Link } from 'react-router-dom';
import 'react-accessible-accordion/dist/fancy-example.css';
import {
  fixDecimals,
  getDataScope,
  getKeyMetricFromHeaderAllMetrics,
  isBrownsUser,
  isCC,
  isIn,
  minsToTime,
  prePathViewPage
} from '../../../helpers';
import Table, { HeadsType, RowType } from '../../Shared/CustomTable';
import { 
  ACCELS_AT_HEADERS, ACCELS_DECELS_HEADERS, ACCELS_DECELS_ROWS, CUSTOM_ZONE_HEADERS, CUSTOM_ZONE_ROWS, DECELS_AT_HEADERS, EMPTY_ARRAY, 
  FILTER_KEYS, GAME, LATERAL_LOAD_HEADERS, LATERAL_LOAD_ROWS, LIVE_PRACTICE, PAST_PRACTICE, PLAYER_NOTE_LETTER_AND_COLOR, 
  PLAYER_RELATIVE_HEADERS, PLAYER_RELATIVE_ROWS, SHOW_DISTANCE_GRAPHS, 
  TEAM_ZONE_HEADERS, TEAM_ZONE_ROWS, TIMING_PEACE_HEADERS, TIMING_PEACE_ROWS, VIEW } from '../../../config/constants';
import DistanceGraphOnRow from './DistanceGraphOnRow';
import PlayerBallMetricsModal from '../Modal';
import PlayerNoteModal from '../PlayerNoteModal';
import MoreButton from './MoreButton';
import { useDataEventsState, useFiltersState, usePlayersDispatch, usePlayersState, useUserState } from '../../../store/hooks';
import { useRouter } from '../../../helpers/customHooks';
import { CondensedExpanded, PerformanceRoutes, PlayerNotesType } from './types';
import { EventData, RelativeZoneSetup, TeamZoneSetup } from '../../../config/commonTypes';
import excludeLogo  from '../../../assets/close-thick-01.svg';
import {ReactComponent as TextBoxIcon} from '../../../assets/text-box-edit.svg';
import notExcludeLogo  from '../../../assets/close-thick-gray.svg';

export default ({ shownHighlights, hidden, hasUpdate, playerNotes, targetedReprocess, updateTargetedPlayers, targetedPlayers }: CondensedExpanded) => {

  const user = useUserState();
  const { dataEvent: data } = useDataEventsState();
  const {
    eventType,
    pathname,
    history,
    specificScope, params, forcedScopeForFilter
  } = useRouter<PerformanceRoutes>();
  const {
    [FILTER_KEYS.SHOW_DISTANCE_GRAPHS]: showDistanceGraphs,
    [FILTER_KEYS.VIEW]: viewData = VIEW.types[0],
    [FILTER_KEYS.CUSTOM_TABLE]: viewCustom = EMPTY_ARRAY
  } = useFiltersState(forcedScopeForFilter, FILTER_KEYS.SHOW_DISTANCE_GRAPHS, FILTER_KEYS.VIEW, FILTER_KEYS.CUSTOM_TABLE);
  const { excludePlayers } = usePlayersDispatch();
  const { excludedPlayers } = usePlayersState();
  const gameId = data.meta.gameId;
  const [excluded, setExcluded] =  useState(excludedPlayers && excludedPlayers[gameId]? excludedPlayers[gameId] : []);
  const customTotalWidth = eventType !== GAME ? 898 : 931;
  let maxDistance = 0;
  const [showPlayerNote, setShowPlayerNote] = useState(false);
  const [playerId, setPlayerId] = useState('');
  const [playerTeamId, setPlayerTeamId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [playerNote, setPlayerNote] = useState<string|undefined>('');
  const [noteType, setNoteType] = useState<string|undefined>('');
  const dataScope = getDataScope(params.teamId);
  if (data && data[dataScope] && data[dataScope].players) {
    (data[dataScope] as EventData).players.forEach(player => {
      maxDistance = Math.max(maxDistance, (
        +fixDecimals(player.metrics.walkYds, 0) +
        +fixDecimals(player.metrics.jogYds, 0) +
        +fixDecimals(player.metrics.runYds, 0) +
        +fixDecimals(player.metrics.sprintYds, 0) +
        +fixDecimals(player.metrics.maxSprintYds, 0)
      ));
    });
  }
let teamZoneThreshold = {} as TeamZoneSetup;
let relativeZoneThreshold = {} as RelativeZoneSetup;
let HEADER_THRESHOLDS;
const playerNotesColors: PlayerNotesType[] = [];
if(playerNotes && playerNotes.length !== 0){
  playerNotes.forEach(note=>{
    if(PLAYER_NOTE_LETTER_AND_COLOR.find(type=> type.value === note.type)){
      playerNotesColors.push({
        playerId: note.playerId,
        type: PLAYER_NOTE_LETTER_AND_COLOR.find(type=> type.value === note.type)?.letter,
        color: PLAYER_NOTE_LETTER_AND_COLOR.find(type=> type.value === note.type)?.color,
        note: note.note
      });
    }else{
      playerNotesColors.push({
        playerId: note.playerId,
        type: 'F',
        color: 'green',
        note: note.note
      });
    }
    
  });
}

const formatThreshold = (min?:string, max?:string, unit?:string) => {
  const unitToUse = unit || 'MPH';
  const formattedMin = min?.includes('%') ? min : `${min} ${unitToUse}`;
  const formattedMax = max ? `Max: ${max.includes('%') ? max : `${max} ${unitToUse}`}` : '+';
  return `Min: ${formattedMin} ${formattedMax}`;
};

if (
  data &&
  data[dataScope] &&
  data[dataScope].zones &&
  data[dataScope].zones.teamZoneSetup
) {
  teamZoneThreshold = data[dataScope].zones.teamZoneSetup;
  relativeZoneThreshold = data[dataScope].zones.relativeZoneSetup;
  HEADER_THRESHOLDS = {
    //TEAM ZONE
    "TEAM WALK YDS": formatThreshold(
      teamZoneThreshold.walk?.min,
      teamZoneThreshold.walk?.max
    ),
    "TEAM JOG YDS": formatThreshold(
      teamZoneThreshold.jog?.min,
      teamZoneThreshold.jog?.max
    ),
    "TEAM RUN YDS": formatThreshold(
      teamZoneThreshold.run?.min,
      teamZoneThreshold.run?.max
    ),
    "TEAM SPRINT YDS": formatThreshold(
      teamZoneThreshold.sprint?.min,
      teamZoneThreshold.sprint?.max
    ),
    "TEAM MAX SPRINT YDS": formatThreshold(
      teamZoneThreshold.maxSprint?.min,
      teamZoneThreshold.maxSprint?.max
    ),
    "TEAM WALK TIME": formatThreshold(
      teamZoneThreshold.walk?.min,
      teamZoneThreshold.walk?.max
    ),
    "TEAM JOG TIME": formatThreshold(
      teamZoneThreshold.jog?.min,
      teamZoneThreshold.jog?.max
    ),
    "TEAM RUN TIME": formatThreshold(
      teamZoneThreshold.run?.min,
      teamZoneThreshold.run?.max
    ),
    "TEAM SPRINT TIME": formatThreshold(
      teamZoneThreshold.sprint?.min,
      teamZoneThreshold.sprint?.max
    ),
    "TEAM MAX SPRINT TIME": formatThreshold(
      teamZoneThreshold.maxSprint?.min,
      teamZoneThreshold.maxSprint?.max
    ),
    "TEAM HARD EFFORT DIST": `Min: ${teamZoneThreshold.teamHardEffDist?.min} ${
      teamZoneThreshold.teamHardEffDist?.max
        ? "Max: " + teamZoneThreshold.teamHardEffDist.max
        : "+"
    }`,
    "YDS 10+": `${teamZoneThreshold.yardsOfXPlus?.min + " YDS"} ${
      teamZoneThreshold.yardsOfXPlus?.max
        ? "Max: " + teamZoneThreshold.yardsOfXPlus.max
        : "+"
    }`,
    //RELATIVE ZONE
    "REL WALK YDS": formatThreshold(
      relativeZoneThreshold.walk?.min,
      relativeZoneThreshold.walk?.max
    ),
    "REL JOG YDS": formatThreshold(
      relativeZoneThreshold.jog?.min,
      relativeZoneThreshold.jog?.max
    ),
    "REL RUN YDS": formatThreshold(
      relativeZoneThreshold.run?.min,
      relativeZoneThreshold.run?.max
    ),
    "REL SPRINT YDS": formatThreshold(
      relativeZoneThreshold.sprint?.min,
      relativeZoneThreshold.sprint?.max
    ),
    "REL MAX SPRINT YDS": formatThreshold(
      relativeZoneThreshold.maxSprint?.min,
      relativeZoneThreshold.maxSprint?.max
    ),
    "REL WALK TIME": formatThreshold(
      relativeZoneThreshold.walk?.min,
      relativeZoneThreshold.walk?.max
    ),
    "REL JOG TIME": formatThreshold(
      relativeZoneThreshold.jog?.min,
      relativeZoneThreshold.jog?.max
    ),
    "REL RUN TIME": formatThreshold(
      relativeZoneThreshold.run?.min,
      relativeZoneThreshold.run?.max
    ),
    "REL SPRINT TIME": formatThreshold(
      relativeZoneThreshold.sprint?.min,
      relativeZoneThreshold.sprint?.max
    ),
    "REL MAX SPRINT TIME": formatThreshold(
      relativeZoneThreshold.maxSprint?.min,
      relativeZoneThreshold.maxSprint?.max
    ),
    "REL HARD EFFORT DIST": `Min: ${
      relativeZoneThreshold.teamHardEffDist?.min
    } ${
      relativeZoneThreshold.teamHardEffDist?.max
        ? "Max: " + relativeZoneThreshold.teamHardEffDist.max
        : "+"
    }`,
    // RELATIVE COUNTS
    "REL WALK COUNT": formatThreshold(
      relativeZoneThreshold.walk?.min,
      relativeZoneThreshold.walk?.max
    ),
    "REL JOG COUNT": formatThreshold(
      relativeZoneThreshold.jog?.min,
      relativeZoneThreshold.jog?.max
    ),
    "REL RUN COUNT": formatThreshold(
      relativeZoneThreshold.run?.min,
      relativeZoneThreshold.run?.max
    ),
    "REL SPRINT COUNT": formatThreshold(
      relativeZoneThreshold.sprint?.min,
      relativeZoneThreshold.sprint?.max
    ),
    "REL MAX SPRINT COUNT": formatThreshold(
      relativeZoneThreshold.maxSprint?.min,
      relativeZoneThreshold.maxSprint?.max
    ),
    // COUNTS
    "TEAM WALK COUNT": formatThreshold(
      teamZoneThreshold.walk?.min,
      teamZoneThreshold.walk?.max
    ),
    "TEAM JOG COUNT": formatThreshold(
      teamZoneThreshold.jog?.min,
      teamZoneThreshold.jog?.max
    ),
    "TEAM RUN COUNT": formatThreshold(
      teamZoneThreshold.run?.min,
      teamZoneThreshold.run?.max
    ),
    "TEAM SPRINT COUNT": formatThreshold(
      teamZoneThreshold.sprint?.min,
      teamZoneThreshold.sprint?.max
    ),
    "TEAM MAX SPRINT COUNT": formatThreshold(
      teamZoneThreshold.maxSprint?.min,
      teamZoneThreshold.maxSprint?.max
    ),
  };
}

  const excludePlayer = (id:string) => {
    const index = excluded.indexOf(id);
    if (index > -1) {
        excluded.splice(index, 1);
        setExcluded([...excluded]);
      }else{
        setExcluded([...excluded, id]);
      }
  };
  useEffect(() => {
    excludePlayers(gameId, excluded);
  }, [JSON.stringify(excluded)]);

  let browns12Label = false;
  if (isBrownsUser(user)) {
    browns12Label = true;
  } else if (eventType === GAME && data.meta.homeTeam.teamId === '1050' && isCC(user)) {
    browns12Label = true;
  }

  const CUSTOM_HEADERS: string[] =[];
  const CUSTOM_ROWS: string[] =[];

  if(viewData.value === VIEW.types[10].value){
    viewCustom.forEach((data)=>{
      if(data){
      if(data.name.split('.')[0] === '0'){
        CUSTOM_HEADERS.push(TEAM_ZONE_HEADERS[data.name.split('.')[1]]);
        CUSTOM_ROWS.push(TEAM_ZONE_ROWS[data.name.split('.')[1]]);
      };
      if(data.name.split('.')[0] === '1'){
        CUSTOM_HEADERS.push(PLAYER_RELATIVE_HEADERS[data.name.split('.')[1]]);
        CUSTOM_ROWS.push(PLAYER_RELATIVE_ROWS[data.name.split('.')[1]]);
      };
      if(data.name.split('.')[0] === '2'){
        CUSTOM_HEADERS.push(TIMING_PEACE_HEADERS[data.name.split('.')[1]]);
        CUSTOM_ROWS.push(TIMING_PEACE_ROWS[data.name.split('.')[1]]);
    };
      if(data.name.split('.')[0] === '3'){
        CUSTOM_HEADERS.push(+data.name.split('.')[1] < 22 ? ACCELS_DECELS_HEADERS[data.name.split('.')[1]] :
      +data.name.split('.')[1] < 28 ? ACCELS_AT_HEADERS[data.name.split('.')[1]-22]: DECELS_AT_HEADERS[data.name.split('.')[1]-28]);
      CUSTOM_ROWS.push(ACCELS_DECELS_ROWS[data.name.split('.')[1]]);
    }
    if(data.name.split('.')[0] === '4'){
      CUSTOM_HEADERS.push(LATERAL_LOAD_HEADERS[data.name.split('.')[1]]);
      CUSTOM_ROWS.push(LATERAL_LOAD_ROWS[data.name.split('.')[1]]);
    };
    if(data.name.split('.')[0] === '5'){
      CUSTOM_HEADERS.push(CUSTOM_ZONE_HEADERS[data.name.split('.')[1]]);
      CUSTOM_ROWS.push(CUSTOM_ZONE_ROWS[data.name.split('.')[1]]);
    };
  }
    });
    if(CUSTOM_HEADERS.length < 12){
      CUSTOM_HEADERS.push(' ');
    }
    if(CUSTOM_ROWS.length < 12){
      CUSTOM_ROWS.push(' ');
    }
  }
  if(viewData.value === VIEW.types[6].value && LATERAL_LOAD_HEADERS.length <= 8){
    LATERAL_LOAD_HEADERS.push(' ');
    LATERAL_LOAD_ROWS.push(' ');

  }else if(LATERAL_LOAD_HEADERS.length > 8 && LATERAL_LOAD_ROWS.length>8 && viewData.value !== VIEW.types[6].value){
    LATERAL_LOAD_HEADERS.splice(-1);
    LATERAL_LOAD_ROWS.splice(-1);
  }
  
  const headers: HeadsType<string, JSX.Element> = [
    ...((targetedReprocess) ?
    [
      { 
      print: ()=> <i className='mdi mdi-checkbox-marked-outline' style={{fontSize: '15px', fontWeight: 'bold'}}/> ,
      style: { width: '33px' }, isColumnFixedY: true }

    ]:[
      { style: { width: '33px' }, isPinFlagCell: true, isSelectable: true, isColumnFixedY: true },
    ]),
    ...((eventType !== GAME) ?
            [
             {style:{width: '33px'} ,content: 'EX.', isOrderly: true, isColumnFixedY: true}

            ]:[]),
    {
      style: { width: '117px', textAlign: 'left', position: 'relative' },
      content: 'PLAYER',
      isOrderly: true,
      isColumnFixedY: true
    },
    ...((eventType !== GAME  || isCC(user) || user.teamId === params.teamId) ?
          [
            { style: { width: '60px' }, content: 'STATUS', isOrderly: true, isColumnFixedY: true },
          ]:[]),
    { style: { width: '50px' }, content: 'NO.', isOrderly: true, isColumnFixedY: true },
    { style: { width: '80px' }, content: 'POS.', isOrderly: true, isColumnFixedY: true, rightShadow: true },
    ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[1].value) ?
      [...TEAM_ZONE_HEADERS.map((content, index) => {
        return {
          content: content ===  'YDS 10+'?  browns12Label? 'YDS 12+' : content :
          content ===  'TIME 10+'? browns12Label? 'TIME 12+' : content : content,
          metric: getKeyMetricFromHeaderAllMetrics(content),
          class: index +1 === TEAM_ZONE_HEADERS.length? 't-ll': {},
          isOrderly: true,
          hoverButton: eventType !== GAME && content !== 'TEAM TARGET MPH' && (
            (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
          )
        };
      }),
      ] 
        :
      []),
    ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[2].value) ?
      [
        ...PLAYER_RELATIVE_HEADERS.map((content, index) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            class: index +1 === PLAYER_RELATIVE_HEADERS.length? 't-ll': {},
            hoverButton: eventType !== GAME && content !== 'REL TARGET MPH' && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        ] :
      []),
      ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[3].value) ?
      [
        ...CUSTOM_ZONE_HEADERS.map((content, index) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            class: index +1 === CUSTOM_ZONE_HEADERS.length? 't-ll': {},
            hoverButton: eventType !== GAME && content !== 'CUST TARGET MPH' && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        ] :
      []),
    ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[4].value) ?
      [
        ...TIMING_PEACE_HEADERS.map((content, index) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            class: index +1 === TIMING_PEACE_HEADERS.length? 't-ll': {},
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
      ] : []),
    ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[5].value) ?
      [
        ...ACCELS_DECELS_HEADERS.map((content, index) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            class: index +1 === ACCELS_DECELS_HEADERS.length? 't-ll': {},
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        ...ACCELS_AT_HEADERS.map((content) => {
      return {
        content,
        style: { width: '70px', textAlign: 'center', position: 'relative' },
        metric: getKeyMetricFromHeaderAllMetrics(content),
        isOrderly: true,
        hoverButton: eventType !== GAME && (
          (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
        )
      };
    }),
    ...DECELS_AT_HEADERS.map((content, index) => {
      return {
        content,
        style: { width: '65px', textAlign: 'center', position: 'relative' },
        textAlign: 'center',
        metric: getKeyMetricFromHeaderAllMetrics(content),
        class: index +1 === DECELS_AT_HEADERS.length? 't-ll': {},
        isOrderly: true,
        hoverButton: eventType !== GAME && (
          (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
        )
      };
    }),
  ] :
  []),
    ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[6].value) ?
      [
        ...LATERAL_LOAD_HEADERS.map((content, index) => {
          return {
            content,
            style: index+1 === LATERAL_LOAD_HEADERS.length && viewData.value === VIEW.types[6].value? {width:`${(customTotalWidth - (385))}px`}: 
            ['CUMULATIVE LOAD', 'LOAD BIAS'].includes(content)? { width: '73px', textAlign: 'center', position: 'relative' } : 
            ['RIGHT MEDIUM LATERAL LOAD'].includes(content)? { width: '89.5px', textAlign: 'center', position: 'relative' } :
            { width: '89px', textAlign: 'center', position: 'relative' },
            textAlign: 'center',
            class: index +1 === LATERAL_LOAD_HEADERS.length? 't-ll': {},
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: index+1 === LATERAL_LOAD_HEADERS.length && viewData.value === VIEW.types[6].value? false : true,
            hoverButton: eventType !== GAME && content !== ' ' &&(
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
          ] :
          []),

    ...((viewData.value === VIEW.types[7].value) ?
      [
        ...['TOTAL DIST',
          'TEAM WALK YDS',
          'TEAM JOG YDS',
          'TEAM RUN YDS',
          'TEAM SPRINT YDS',
          'TEAM MAX SPRINT YDS',
          'TEAM HARD EFFORT DIST'].map((content) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        ...['TEAM WALK COUNT',
          'TEAM JOG COUNT',
          'TEAM RUN COUNT'].map((content) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        ...['TEAM SPRINT COUNT',
          'TEAM MAX SPRINT COUNT',
          'MAX STND MPH',
          'TOTAL ACCELS',
          'TOTAL DECELS',
          'EXPLOSIVE EFFORT'].map((content) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        {
          content: 'EXERTION INDEX',
          metric: getKeyMetricFromHeaderAllMetrics('EXERTION INDEX'),
          isOrderly: true,
          hoverButton: eventType !== GAME && (
            (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
          )
        },
        {
          content: 'PLAYER LOAD',
          metric: getKeyMetricFromHeaderAllMetrics('PLAYER LOAD'),
          class: 't-ll',
          isOrderly: true,
          hoverButton: eventType !== GAME && (
            (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
          )
        }
      ] :
      []),
    ...((viewData.value === VIEW.types[8].value) ?
      [
        ...['TOTAL DIST',
          'REL WALK YDS',
          'REL JOG YDS',
          'REL RUN YDS',
          'REL SPRINT YDS',
          'REL MAX SPRINT YDS',
          'REL HARD EFFORT DIST'].map((content) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        ...['REL WALK COUNT',
          'REL JOG COUNT',
          'REL RUN COUNT'].map((content) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        ...['REL SPRINT COUNT',
          'REL MAX SPRINT COUNT',
          'MAX STND MPH',
          'TOTAL ACCELS',
          'TOTAL DECELS',
          'EXPLOSIVE EFFORT'].map((content) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        {
          content: 'EXERTION INDEX',
          metric: getKeyMetricFromHeaderAllMetrics('EXERTION INDEX'),
          isOrderly: true,
          hoverButton: eventType !== GAME && (
            (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
          )
        },
        {
          content: 'PLAYER LOAD',
          metric: getKeyMetricFromHeaderAllMetrics('PLAYER LOAD'),
          class: 't-ll',
          isOrderly: true,
          hoverButton: eventType !== GAME && (
            (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
          )
        }

      ] :
      []),
      ...((viewData.value === VIEW.types[9].value) ?
      [
        ...['TOTAL DIST',
          'CUST WALK YDS',
          'CUST JOG YDS',
          'CUST RUN YDS',
          'CUST SPRINT YDS',
          'CUST MAX SPRINT YDS',
          'CUST HARD EFFORT YDS'].map((content) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        ...['CUST WALK COUNT',
          'CUST JOG COUNT',
          'CUST RUN COUNT'].map((content) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        ...['CUST SPRINT COUNT',
          'CUST MAX SPRINT COUNT',
          'MAX STND MPH',
          'TOTAL ACCELS',
          'TOTAL DECELS',
          'EXPLOSIVE EFFORT'].map((content) => {
          return {
            content,
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            hoverButton: eventType !== GAME && (
              (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
            )
          };
        }),
        {
          content: 'EXERTION INDEX',
          metric: getKeyMetricFromHeaderAllMetrics('EXERTION INDEX'),
          isOrderly: true,
          hoverButton: eventType !== GAME && (
            (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
          )
        },
        {
          content: 'PLAYER LOAD',
          metric: getKeyMetricFromHeaderAllMetrics('PLAYER LOAD'),
          class: 't-ll',
          isOrderly: true,
          hoverButton: eventType !== GAME && (
            (metric: string) => <MoreButton key={'more-e-' + String(metric)} metric={metric} />
          )
        }

      ]
      :
      (viewData.value === VIEW.types[10].value)?
      [
        ...CUSTOM_HEADERS.map((content, index) => {
          return {
            content:
              content === "YDS 10+"
                ? browns12Label
                  ? "YDS 12+"
                  : content
                : content === "TIME 10+"
                ? browns12Label
                  ? "TIME 12+"
                  : content
                : content,
            style: {
              ...(["RIGHT MEDIUM LATERAL LOAD"].includes(content)
                ? {
                    width: "89.5px",
                    textAlign: "center",
                    position: "relative",
                  }
                : ["LEFT MEDIUM LATERAL LOAD"].includes(content)
                ? {
                    width: "89px",
                    textAlign: "center",
                    position: "relative",
                  }
                : {}),
              ...(CUSTOM_HEADERS.length < 12
                ? index + 1 === CUSTOM_HEADERS.length
                  ? {
                      width: `${
                        customTotalWidth - (CUSTOM_HEADERS.length - 1) * 75
                      }px`,
                    }
                  : {}
                : {}),
            },
            metric: getKeyMetricFromHeaderAllMetrics(content),
            isOrderly: true,
            hoverButton:
              eventType !== GAME &&
              !content.includes("TARGET MPH") &&
              content !== " " &&
              ((metric: string) => (
                <MoreButton key={"more-e-" + String(metric)} metric={metric} />
              )),
          };
        })
      ]:
      [])
  ];

  const titleHeaders: HeadsType<JSX.Element> = [{
    style: { width: eventType !== GAME ? '373px': (eventType !== GAME  || isCC(user) || user.teamId === params.teamId)? '340px'  : '280px'},
    isColumnFixedY: true,
    rightShadow: true,
    content: <div>Player</div>
  }];

  if (viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[1].value) {
    titleHeaders.push({ class: 't-ll', style: { width: '1575px' }, content: <div>Team Zone</div> }); // 18*75
  }
  if (viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[2].value) {
    titleHeaders.push({ class: 't-ll', style: { width: '1425px' }, content: <div>Player Relative Zone</div> }); // 16*75
  }
  if (viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[3].value) {
    titleHeaders.push({ class: 't-ll', style: { width: '1425px' }, content: <div>Custom Zone</div> }); // 16*75
  }
  if (viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[4].value) {
    titleHeaders.push({ class: 't-ll', style: { width: '900px' }, content: <div>Timing and Pace</div> }); // 12*75
  }
  if (viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[5].value) {
    titleHeaders.push({ class: 't-ll', style: { width: '2460px' }, content: <div>Accels and Decels</div> }); // 21*75 + new fields
  }
  if (viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[6].value) {
    titleHeaders.push({ class: 't-ll',   style: viewData.value === VIEW.types[6].value? eventType !== GAME ?  { width: '1258.5px' } : 
    { width: '1291.5px' }:{width: '680.5px'} , content: <div>Load Metrics</div> }); // 21*75 + new fields
  }
  if (viewData.value === VIEW.types[7].value) {
    titleHeaders.push({ class: 't-ll', style: { width: '1350px' }, content: <div>Zebra Team Summary</div> }); // 14*75
  }
  if (viewData.value === VIEW.types[8].value) {
    titleHeaders.push({ class: 't-ll', style: { width: '1350px' }, content: <div>Zebra Relative Summary</div> }); // 14*75
  }
  if (viewData.value === VIEW.types[9].value) {
    titleHeaders.push({ class: 't-ll', style: { width: '1350px' }, content: <div>Zebra Custom Summary</div> }); // 14*75
  }
  if (viewData.value === VIEW.types[10].value) {
    titleHeaders.push({ class: 't-ll', 
    style: { width: eventType !== GAME ? '898px' : (eventType !== GAME  || isCC(user) || user.teamId === params.teamId)? '931px' :'931px' }, 
    isCustomHeader: true,
    isColumnFixedY: true,
    content: <div>Custom View</div> 
 },
    );
  }

  const prePath = prePathViewPage(pathname);
  const rows: RowType[] = [];
  const cellPrint = (content: string) => (
    <label style={{ width: '39px', textOverflow: 'ellipsis', overflow: 'hidden' }}>{content}</label>
  );
  if (data && data[dataScope] && data[dataScope].players) {
    (data[dataScope] as EventData).players.forEach((player) => {
      const dataDistanceGraph = [
        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[0]) ?
          fixDecimals(
            viewData.value === VIEW.types[2].value ||
            viewData.value === VIEW.types[6].value ?
              player.metrics.walkYdsRelative : player.metrics.walkYds, 0)
          : 0,

        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[0]) ?
          fixDecimals(
            viewData.value === VIEW.types[2].value ||
            viewData.value === VIEW.types[6].value ?
              player.metrics.jogYdsRelative : player.metrics.jogYds, 0)
          : 0,

        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[0]) ||
        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[1]) ||
        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[2]) ?
          fixDecimals(
            viewData.value === VIEW.types[2].value ||
            viewData.value === VIEW.types[6].value ?
              player.metrics.runYdsRelative : player.metrics.runYds, 0)
          : 0,

        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[0]) ||
        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[1]) ||
        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[3]) ?
          fixDecimals(
            viewData.value === VIEW.types[2].value ||
            viewData.value === VIEW.types[6].value ?
              player.metrics.sprintYdsRelative : player.metrics.sprintYds, 0)
          : 0,

        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[0]) ||
        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[1]) ||
        JSON.stringify(showDistanceGraphs) === JSON.stringify(SHOW_DISTANCE_GRAPHS.types[4]) ?
          fixDecimals(
            viewData.value === VIEW.types[2].value ||
            viewData.value === VIEW.types[6].value ?
              player.metrics.maxSprintYdsRelative : player.metrics.maxSprintYds, 0)
          : 0];
      rows.push({
        columns: [
          ...((targetedReprocess) ?
          [
            { 
            isPinFlagCell: false,
            print: ()=> 
           <>
              <input
                type="checkbox"
                id="cb1"
                checked={targetedPlayers? isIn(targetedPlayers, player.id): false}
                onChange={()=>{if(updateTargetedPlayers)updateTargetedPlayers([player.id]);}}
                
                />
              
            </> ,
            style: { width: '33px' },
            
          
          }
      
          ]:[
            { isPinFlagCell: true, id: 'expanded-flag-' + player.id },
          ]),

          ...((eventType !== GAME) ?
            [

              { content: excluded && excluded[excluded.indexOf(player.id)] === player.id ? 1 : 0,
                isExcludeCell: excluded && excluded[excluded.indexOf(player.id)] === player.id ? true : false,
                id:'exclude-player-' + player.id,
                print: () => excluded && excluded[excluded.indexOf(player.id)] === player.id ? <img src={excludeLogo} width='17px' id={'mdi mdi-close-icon' + player.id}
                onClick={()=>excludePlayer(player.id)}/>: <img src={notExcludeLogo} width='17px' id={'mdi mdi-close-icon' + player.id}
                onClick={()=>excludePlayer(player.id)}/>,
                order: (contentA: number, contentB: number) => contentA - contentB
              }

            ]:[]),

          {
            // player-img
            style: {
              // backgroundImage: `url(https://static.nfl.com/static/content/public/static/img/fantasy/transparent/200x200/${player.esbId}.png)`,// 0% 0% / 50px no-repeat rgb(255, 255, 255)`,
              fontWeight: 'bold',
              backgroundSize: '50px',
              backgroundRepeat: 'no-repeat'
            },
            content: player.name,
            print: (content) => {
              return <Link to={{ pathname: prePath + '/modal/' + player.id, state: history.location.state }}>
                <div
                  style={{
                    width: '97px',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden'
                  }}
                >{content}</div>
              </Link>;
            },
            order: (contentA, contentB) => {
              if (contentA.split(' ').length < 2) {
                return contentA.localeCompare(contentB);
              }
              const lastNameA = contentA.split(' ')[1];
              const lastNameB = contentB.split(' ')[1];
              return lastNameA.localeCompare(lastNameB);
            },
          },
          ...((eventType !== GAME  || isCC(user) || user.teamId === params.teamId) ?
            [
              {
                content:{type: playerNotesColors && playerNotesColors.find((e:PlayerNotesType)=> e.playerId === player.id)?.type? 
                  playerNotesColors.find((e:PlayerNotesType)=> e.playerId === player.id)?.type : 'F', sort: player.jerseyNumber},
                style: {width: '60px' },
                print:( content:{type:string, sort:number})=>(
                <>
                  <label style={{background: playerNotesColors.find(e => e.playerId === player.id)?.color? playerNotesColors.find(e => e.playerId === player.id)?.color :'green', 
                  color: content.type === 'M' || content.type === 'Q' ? 'black' : 'white', width: '15px', borderRadius: '25px', textAlign: 'center', fontWeight: 'bold', marginRight: '10px'}}>
                    {content.type}
                  </label>
                  <button style={{border: 'none', background: 'none', padding:'0', position: 'relative'}} onClick={()=>{
                    setPlayerId(player.id);
                    setShowPlayerNote(true);
                    setPlayerTeamId(player.teamId);
                    setPlayerName(player.name);
                    setPlayerNote(playerNotes.find((e:PlayerNotesType)=> e.playerId === player.id)?.note);
                    setNoteType(playerNotes.find((e:PlayerNotesType)=> e.playerId === player.id)?.type);}}>
                      <TextBoxIcon width={'17px'} fill={playerNotes && playerNotes.find((e:PlayerNotesType)=> e.playerId === player.id) && 
                        playerNotes.find((e:PlayerNotesType)=> e.playerId === player.id)?.note ?'#007ABA':''}/>
                  </button>
                </>
                ),
                order: (contentA:{type:string, sort:number}, contentB:{type:string, sort:number}) => {
                  if (contentA.type !== contentB.type) {
                    return contentA.type.localeCompare(contentB.type);
                  }
                  return  contentB.sort - contentA.sort;
                },
              },

            ]:[]),
          {
            content: player.jerseyNumber,
            print: (content) => (<Link to={{ pathname: prePath + '/modal/' + player.id }}>{'#' + content}</Link>),
            order: (contentA: string, contentB: string) => {
              const jerseyA =  contentA.replace(/\D/g, '');
              const jerseyB =  contentB.replace(/\D/g, '');
              const jerseyLetterA = contentA.replace(jerseyA, '');
              const jerseyLetterB = contentB.replace(jerseyB, '');
              if (+jerseyA !== +jerseyB) {
                return +jerseyB- +jerseyA;
                 
              }
              return jerseyLetterB.localeCompare(jerseyLetterA);
              
            }
          },
          {
            class: 'padding-cell-special',
            content: player.position,
            print: (content) => (<Link to={{ pathname: prePath + '/modal/' + player.id }}>{player.position}</Link>)
          },
          ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[1].value) ?
            [
              ...(TEAM_ZONE_ROWS.map((key,index) => {
                return {
                  class: index +1 === TEAM_ZONE_ROWS.length? 't-ll': {},
                  content: key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): key === 'targetMPH'? fixDecimals(player.metrics[key],1) : fixDecimals(player.metrics[key],0),
                  title: key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): key === 'targetMPH'? fixDecimals(player.metrics[key],1) : fixDecimals(player.metrics[key],0) ,
                  print: key.includes('time') || key.includes('Time')? (content: string) => (<label
                    style={ key.includes('time') || key.includes('Time')? { width: '50px', textOverflow: 'ellipsis', overflow: 'hidden' }: {}}
                  >{content}</label>): cellPrint
                };
              })),
            ] : []),
          ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[2].value) ?
            [
              ...(PLAYER_RELATIVE_ROWS.map((key, index) => {
                return {
                  class: index +1 === PLAYER_RELATIVE_ROWS.length? 't-ll': {},
                  content: key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): 
                  key === 'targetMPHRelative'? fixDecimals(player.metrics[key], 1) : fixDecimals(player.metrics[key], 0),
                  title: key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): 
                  key === 'targetMPHRelative'? fixDecimals(player.metrics[key], 1) : fixDecimals(player.metrics[key], 0),
                  print: key.includes('time') || key.includes('Time')? (content: string) => (<label
                    style={{ width: '50px', textOverflow: 'ellipsis', overflow: 'hidden' }}
                  >{content}</label>): cellPrint
                };
              })),
             
            ] : []),
            ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[3].value) ?
            [
              ...(CUSTOM_ZONE_ROWS.map((key, index) => {
                return {
                  class: index +1 === CUSTOM_ZONE_ROWS.length? 't-ll': {},
                  content: key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): 
                  key === 'targetMPHCustom'? fixDecimals(player.metrics[key], 1) : fixDecimals(player.metrics[key], 0),
                  title: key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): 
                  key === 'targetMPHCustom'? fixDecimals(player.metrics[key], 1) : fixDecimals(player.metrics[key], 0),
                  print: key.includes('time') || key.includes('Time')? (content: string) => (<label
                    style={{ width: '50px', textOverflow: 'ellipsis', overflow: 'hidden' }}
                  >{content}</label>): cellPrint
                };
              })),
             
            ] : []),
          ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[4].value) ?
            [
              ...(TIMING_PEACE_ROWS.map((key,index) => {
              return {
                class: index +1 === TIMING_PEACE_ROWS.length? 't-ll': {},
                content: key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): 
                key === 'ypm' || key ===  'actYPS'|| key ===  'actMPH' ? fixDecimals(player.metrics[key], 1) : 
                key === 'accelPerMin' || key ===  'deccelPerMin'|| key === 'maxSpeedYPS' || key ===  'maxSpeedMPH' ||
                key ===  'maxSustainedSpeedYPS' || key ===  'maxSustainedSpeedMPH'? fixDecimals(player.metrics[key], 2)
                :fixDecimals(player.metrics[key], 0),
                title: key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): fixDecimals(player.metrics[key], 0),
                print: key.includes('time') || key.includes('Time')? (content: string) => (<label
                  style={{ width: '50px', textOverflow: 'ellipsis', overflow: 'hidden' }}
                >{content}</label>): cellPrint
              };
              })),
            ] : []),
          ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[5].value) ?
            [ 
              ...(ACCELS_DECELS_ROWS.map((key, index) => {
                return {
                  class: index +1 === ACCELS_DECELS_ROWS.length || key === 'maxSustDecel' ? 't-ll': {},
                  content: key === 'totalsAccel' ? fixDecimals(player.metrics.accelSlow + player.metrics.accelMed + player.metrics.accelFast, 0) :
                  key === 'totalsDecel' ? fixDecimals(player.metrics.deccelSlow + player.metrics.deccelMed + player.metrics.deccelFast, 0) :
                  key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): 
                  key.includes('max') || key.includes('ex') || key.includes('Load')? fixDecimals(player.metrics[key], 2) : fixDecimals(player.metrics[key], 0),
                  title: key === 'totalsAccel' ? fixDecimals(player.metrics.accelSlow + player.metrics.accelMed + player.metrics.accelFast, 0) : 
                  key === 'totalsDecel' ? fixDecimals(player.metrics.deccelSlow + player.metrics.deccelMed + player.metrics.deccelFast, 0) :
                  key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): 
                  key.includes('max') || key.includes('ex') || key.includes('Load')? fixDecimals(player.metrics[key], 2) : fixDecimals(player.metrics[key], 0),
                  print: key.includes('time') || key.includes('Time')? (content: string) => (<label
                    style={{ width: '50px', textOverflow: 'ellipsis', overflow: 'hidden' }}
                  >{content}</label>): cellPrint
                };
              })),
            ] : []),
            ...((viewData.value === VIEW.types[0].value || viewData.value === VIEW.types[6].value) ?
            [ 
              ...(LATERAL_LOAD_ROWS.map((key, index) => {
                return {
                  class: index +1 === LATERAL_LOAD_ROWS.length ? 't-ll': {},
                  style: index+1 === LATERAL_LOAD_ROWS.length && viewData.value === VIEW.types[6].value? {width:`${(customTotalWidth - (385))}px`} : {},
                  content: key === ' '? '': key === 'cumulativeBalanceLoad' || key === 'biasedBalanceLoad'? fixDecimals(player.metrics[key],2) : fixDecimals(player.metrics[key],0),
                  title: key === 'cumulativeBalanceLoad' || key === 'biasedBalanceLoad'? fixDecimals(player.metrics[key],2) : fixDecimals(player.metrics[key],0),
                  print: cellPrint
                };
              })),
            ] : []),

          ...((viewData.value === VIEW.types[7].value) ?
            [
              ...(['totalDis',
                'walkYds',
                'jogYds',
                'runYds',
                'sprintYds',
                'maxSprintYds',
                'hardEffortDist',
                'walkCount',
                'jogCount',
                'runCount',
                'sprintCount',
                'maxSprintCount'].map((key) => {
                return {
                  content: fixDecimals(player.metrics[key], 0),
                  title: fixDecimals(player.metrics[key], 0),
                  print: cellPrint
                };
              })),
              ...(['maxSustainedSpeedMPH'].map((key) => {
                return {
                  content: fixDecimals(player.metrics[key], 2),
                  title: fixDecimals(player.metrics[key], 2),
                  print: cellPrint
                };
              })),
              //TOTAL ACCELS
              {
                content: fixDecimals(player.metrics.accelSlow + player.metrics.accelMed + player.metrics.accelFast, 0),
                title: fixDecimals(player.metrics.accelSlow + player.metrics.accelMed + player.metrics.accelFast, 0),
                print: cellPrint
              },
              // TOTAL DECELS
              {
                content: fixDecimals(player.metrics.deccelSlow + player.metrics.deccelMed + player.metrics.deccelFast, 0),
                title: fixDecimals(player.metrics.deccelSlow + player.metrics.deccelMed + player.metrics.deccelFast, 0),
                print: cellPrint
              },
              // EXPLOSIVE EFFORT
              {
                content: fixDecimals(player.metrics.accelFast + player.metrics.deccelFast, 0),
                title: fixDecimals(player.metrics.accelFast + player.metrics.deccelFast, 0),
                print: cellPrint
              },
              // EXERTION INDEX
              {
                content: fixDecimals(player.metrics.exIndex, 2), title: fixDecimals(player.metrics.exIndex, 2),
                print: cellPrint
              },
              {
                content: fixDecimals(player.metrics.zLoad, 2), title: fixDecimals(player.metrics.zLoad, 2),
                print: cellPrint,
                class: 't-ll'
              }
            ] : []),
          ...((viewData.value === VIEW.types[8].value) ?
            [
              ...(['totalDis',
                'walkYdsRelative',
                'jogYdsRelative',
                'runYdsRelative',
                'sprintYdsRelative',
                'maxSprintYdsRelative',
                'hardEffortDistRelative',
                'walkCountRelative',
                'jogCountRelative',
                'runCountRelative',
                'sprintCountRelative',
                'maxSprintCountRelative'].map((key) => {
                return {
                  content: fixDecimals(player.metrics[key], 0),
                  title: fixDecimals(player.metrics[key], 0),
                  print: cellPrint
                };
              })),
              ...(['maxSustainedSpeedMPH'].map((key) => {
                return {
                  content: fixDecimals(player.metrics[key], 2),
                  title: fixDecimals(player.metrics[key], 2),
                  print: cellPrint
                };
              })),
              //TOTAL ACCELS
              {
                content: fixDecimals(player.metrics.accelSlow + player.metrics.accelMed + player.metrics.accelFast, 0),
                title: fixDecimals(player.metrics.accelSlow + player.metrics.accelMed + player.metrics.accelFast, 0),
                print: cellPrint
              },
              // TOTAL DECELS
              {
                content: fixDecimals(player.metrics.deccelSlow + player.metrics.deccelMed + player.metrics.deccelFast, 0),
                title: fixDecimals(player.metrics.deccelSlow + player.metrics.deccelMed + player.metrics.deccelFast, 0),
                print: cellPrint
              },
              // EXPLOSIVE EFFORT
              {
                content: fixDecimals(player.metrics.accelFast + player.metrics.deccelFast, 0),
                title: fixDecimals(player.metrics.accelFast + player.metrics.deccelFast, 0),
                print: cellPrint
              },
              // EXERTION INDEX
              {
                content: fixDecimals(player.metrics.exIndex, 2), title: fixDecimals(player.metrics.exIndex, 2),
                print: cellPrint
              },
              //PLAYER LOAD
              {
                content: fixDecimals(player.metrics.zLoad, 2), title: fixDecimals(player.metrics.zLoad, 2),
                print: cellPrint,
                class: 't-ll'
              }
            ]
            :
            []),
            ...((viewData.value === VIEW.types[9].value) ?
            [
              ...([  'totalDis',
                'walkYdsCustom',
                'jogYdsCustom',
                'runYdsCustom',
                'sprintYdsCustom',
                'maxSprintYdsCustom',
                'hardEffortDistCustom',
                'walkCountCustom',
                'jogCountCustom',
                'runCountCustom',
                'sprintCountCustom',
                'maxSprintCountCustom'].map((key) => {
                return {
                  content: fixDecimals(player.metrics[key], 0),
                  title: fixDecimals(player.metrics[key], 0),
                  print: cellPrint
                };
              })),
              ...(['maxSustainedSpeedMPH'].map((key) => {
                return {
                  content: fixDecimals(player.metrics[key], 2),
                  title: fixDecimals(player.metrics[key], 2),
                  print: cellPrint
                };
              })),
              //TOTAL ACCELS
              {
                content: fixDecimals(player.metrics.accelSlow + player.metrics.accelMed + player.metrics.accelFast, 0),
                title: fixDecimals(player.metrics.accelSlow + player.metrics.accelMed + player.metrics.accelFast, 0),
                print: cellPrint
              },
              // TOTAL DECELS
              {
                content: fixDecimals(player.metrics.deccelSlow + player.metrics.deccelMed + player.metrics.deccelFast, 0),
                title: fixDecimals(player.metrics.deccelSlow + player.metrics.deccelMed + player.metrics.deccelFast, 0),
                print: cellPrint
              },
              // EXPLOSIVE EFFORT
              {
                content: fixDecimals(player.metrics.accelFast + player.metrics.deccelFast, 0),
                title: fixDecimals(player.metrics.accelFast + player.metrics.deccelFast, 0),
                print: cellPrint
              },
              // EXERTION INDEX
              {
                content: fixDecimals(player.metrics.exIndex, 2), title: fixDecimals(player.metrics.exIndex, 2),
                print: cellPrint
              },
              // PLAYER LOAD
              {
                content: fixDecimals(player.metrics.zLoad, 2), title: fixDecimals(player.metrics.zLoad, 2),
                print: cellPrint,
                class: 't-ll'
              }
            ]
            :
            (viewData.value === VIEW.types[10].value)?
            [
              ...CUSTOM_ROWS.map((key,index)=>{
                return{
                  style: CUSTOM_ROWS.length <12? index+1 === CUSTOM_ROWS.length? {width:`${(customTotalWidth - ((CUSTOM_ROWS.length-1) *75))}px`}:{}:{},
                  content: key === ' '? '' :key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]) : 
                   key.includes('LatLoad') || key.includes('maxSprint') || key.includes('explosiveEffort') ? fixDecimals(player.metrics[key], 0) :
                  key === 'totalsAccel' ? fixDecimals(player.metrics.accelSlow + player.metrics.accelMed + player.metrics.accelFast, 0) :
                  key === 'totalsDecel' ? fixDecimals(player.metrics.deccelSlow + player.metrics.deccelMed + player.metrics.deccelFast, 0) :
                  key.includes('PerMin')? fixDecimals(player.metrics[key], 2) :
                  key ===  'maxSustainedSpeedYPS' || key ===  'maxSustainedSpeedMPH' ||key.includes('max') || key.includes('ex') || key.includes('Load')? 
                  fixDecimals(player.metrics[key], 2) : 
                  key.includes('targetMPH') || key === 'ypm' || key ===  'actYPS'|| key ===  'actMPH'  ? fixDecimals(player.metrics[key], 1 ) : 
                  fixDecimals(player.metrics[key], 0),
                  title: key === 'totalsAccel' ? fixDecimals(player.metrics.accelSlow + player.metrics.accelMed + player.metrics.accelFast, 0) :
                  key === 'totalsDecel' ? fixDecimals(player.metrics.deccelSlow + player.metrics.deccelMed + player.metrics.deccelFast, 0) :
                  key.includes('time') || key.includes('Time')? minsToTime(player.metrics[key]): 
                  key ===  'maxSustainedSpeedYPS' || key ===  'maxSustainedSpeedMPH' ||key.includes('max') || key.includes('ex') || key.includes('Load')? 
                  fixDecimals(player.metrics[key], 2) : 
                  key.includes('targetMPH') || key === 'ypm' || key ===  'actYPS'|| key ===  'actMPH'  ? fixDecimals(player.metrics[key], 1 ) : 
                  fixDecimals(player.metrics[key], 0),
                  print: key.includes('time') || key.includes('Time')? (content: string) => (<label
                    style={{ width: '50px', textOverflow: 'ellipsis', overflow: 'hidden' }}
                  >{content}</label>): cellPrint
                };
              })
            ]
            :
             [])
        ],
        style: {textAlign: 'center'},
        childrenBelow: showDistanceGraphs && (
          <DistanceGraphOnRow
            totalDis={player.metrics.totalDis}
            maxDistance={maxDistance}
            showDistanceGraphs={showDistanceGraphs}
            dataDistanceGraph={dataDistanceGraph as number[]}
          />
        ),
        id: player.id
      });
    });
  }
  const specificCondensedScope = (specificScope || '').split('EXPANDED').join('CONDENSED');

  return (
    <>
      {params.modal && params.playerId &&
      <PlayerBallMetricsModal
        initialTab={'performance-01'}
        show
        onHide={() => (history.push({pathname: prePath + '/page', state: history.location.state }))}
        hidden={hidden}
        hasUpdate={hasUpdate}
      />}
      {playerId && gameId && (eventType !== GAME  || isCC(user) || user.teamId === params.teamId) && 
      <PlayerNoteModal
        show={showPlayerNote}
        close={()=>setShowPlayerNote(false)}
        playerId={playerId}
        gameId={gameId}
        teamId={playerTeamId}
        playerName={playerName}
        playerNote={playerNote}
        playerNoteType={noteType}
        isGame={eventType === GAME}
       />}
      <div
        className="practice-body"
        style={{ height: shownHighlights ? 'calc(100vh - 240px)' : 'calc(100vh - 202px)' }}
      >
        <Table
          forceScope={viewData.value === VIEW.types[9].value? eventType !== GAME? 'ZEBRA_CUSTOM_TABLE': 'CUSTOM_TABLE_GAME' : 
            viewData.value === VIEW.types[10].value? eventType !== GAME? 'CUSTOM_TABLE': 'CUSTOM_TABLE_GAME' : 
          viewData.value === VIEW.types[4].value || viewData.value === VIEW.types[6].value? 'EXPANDED_VIEW_NO_SCROLL' :  eventType !== GAME? 'EXPANDED_VIEW' :
          isCC(user) || user.teamId === params.teamId? 'GAME_EXPANDED_VIEW' : 'GAME_EXPANDED_VIEW_ADMIN' }
          classTable={'default-table-width' + viewData.value === VIEW.types[6].value && eventType === GAME?'expanded-view ' + viewData.value.split(' ').join('-')+'-Game':
          'expanded-view ' + viewData.value.split(' ').join('-')}
          headers={ headers}
          titleHeaders={titleHeaders}
          rows={rows}
          default={{ orderColumn: viewData.value === VIEW.types[9].value? eventType !== GAME?  7 : 6 : viewData.value === VIEW.types[10].value? eventType !== GAME?  -3 : -2 : eventType !== GAME? 8 :
            isCC(user) || user.teamId === params.teamId? 7: 6 }}
          linkPinnedRows={[specificCondensedScope]}
          isPersistent={data.meta.type === LIVE_PRACTICE || data.meta.type === PAST_PRACTICE}
          isPinnedStyle
          hasHeadersTooltip
          zonesThresholds={HEADER_THRESHOLDS}
          customRows={viewData.value === VIEW.types[10].value? viewCustom.length : undefined}
          isTargeted={targetedReprocess}
          hasNotes={(eventType !== GAME  || isCC(user) || user.teamId === params.teamId)}
        />
      </div>
    </>
  );
};
